import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as _ from 'lodash-es';
import { UiService } from './core.ui.service';
import { UserRest } from './core.user.rest.service';
import * as $ from 'jquery';
import * as moment from 'moment';
import { StateService } from '@uirouter/angular';
import { Base64 } from './core.base64.service';
import { TokenService } from './core.token.service';
import { GfTranslateService } from './core.translate.service';
import { PollService } from './core.poll.service';
import { SiteConfigService } from './core.site-config.service';
import { RefusedError } from './core.refused-error';
import { marker } from './ngx-translate-extract-marker';

@Injectable()
export class UserService {
    currentUserPromise;
    _user;
    _darkMode;
    _tiEditVersion;
    _stickyTop;
    _logonDestination;
    _lastLocation;
    _welcomed;
    _hasProjects;
    _lastLocationExceptions;
    _lastVersionCheck;
    msg;
    messages;
    constants;

    constructor(
        private http: HttpClient,
        private uiService: UiService,
        private pollService: PollService,
        private userRest: UserRest,
        private window: Window,
        private stateService: StateService,
        private base64: Base64,
        private tokenService: TokenService,
        private gfTranslateService: GfTranslateService,
        private siteConfigService: SiteConfigService
    ) {
        this.setConstants();
    }

    private setConstants() {
        this.currentUserPromise = null;
        this._user = null;
        this._darkMode = null;
        this._tiEditVersion = 2;
        this._stickyTop = null;
        this._logonDestination = 'todo';
        this._lastLocation = {
            name: 'home',
            params: {}
        };
        this._welcomed = 0;
        this._hasProjects = false;
        this._lastLocationExceptions = [
            'home.user',
            'home.user.welcome',
            'home.user.changepassword',
            'home.user.login',
            'home.user.lostpassword',
            'home.user.register',
            'home.user.reset',
            'home.user.regrequest',
            'home.user.edit',
            'home.user.ssh'
        ];
        this.msg = '';
        this.messages = {
            credentials_not_match: {
                title: marker('An error has occurred'),
                description: marker('Sorry, the username or password you entered are invalid')
            },
            user_data_failure: {
                title: marker('An error has occurred'),
                description: marker('Sorry, could not fetch the user data')
            },
            user_post_failure: {
                title: marker('An error has occurred'),
                description: marker('Sorry, could not save user profile')
            },
            user_post_success: {
                title: marker('Success'),
                description: marker('User profile updated')
            },
            project_data_failure: {
                title: marker('An error has occurred'),
                description: marker('Sorry, could not fetch the list of projects for this user')
            },
            log_out_success: {
                title: marker('Logged out'),
                description: marker('You have been logged out of GForge')
            },
            log_out_failure: {
                title: marker('Logout error'),
                description: marker('There was a problem trying to log you out of GForge')
            },
            language_changed_success: {
                title: marker('Language change'),
                description: marker('Successfully changed language to ')
            },
            language_changed_error: {
                title: marker('Language change error'),
                description: marker('There was an error trying to save the language change')

            },
            ssh_key_error_post: {
                title: marker('Ssh key error'),
                description: marker('There was an error trying to save the ssh key')
            },
            ssh_key_error_get: {
                title: marker('Ssh key error'),
                description: marker('There was an error trying to get the ssh key')
            }
        };
        this.constants = {
            nobody: {
                id: 100,
                unixName: 'None',
                firstName: this.gfTranslateService.translate("Nobody"),
                lastName: ' '
            }
        };
    }

    //Returns 'session' sub-namespace
    get session() {
        var _self = this;

        function loginProxy(_self) {
            return (...args) => { return _self.login(...args) };
        }

        function setNobodyProxy(_self) {
            return (...args) => { return _self.setNobody(...args) };
        }

        function setUserProxy(_self) {
            return (...args) => { return _self.setUser(...args) };
        }

        function logoutProxy(_self) {
            return (...args) => { return _self.logout(...args) };
        }

        function verifyProxy(_self) {
            return (...args) => { return _self.verify(...args) };
        }

        function getUnixNameProxy(_self) {
            return (...args) => { return _self.getUnixName(...args) };
        }

        function getUserIdProxy(_self) {
            return (...args) => { return _self.getUserId(...args) };
        }

        function getUserProxy(_self) {
            return (...args) => { return _self.getUser(...args) };
        }

        function isSiteAdminProxy(_self) {
            return (...args) => { return _self.isSiteAdmin(...args) };
        }

        function recoverProxy(_self) {
            return (...args) => { return _self.recover(...args) };
        }

        function hasUserProxy(_self) {
            return (...args) => { return _self.hasUser(...args) };
        }

        function getAuthMethodProxy(_self) {
            return (...args) => { return _self.getAuthMethod(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            login: loginProxy(_self),
            setNobody: setNobodyProxy(_self),
            setUser: setUserProxy(_self),
            logout: logoutProxy(_self),
            verify: verifyProxy(_self),
            getUnixName: getUnixNameProxy(_self),
            getUserId: getUserIdProxy(_self),
            getUser: getUserProxy(_self),
            isSiteAdmin: isSiteAdminProxy(_self),
            recover: recoverProxy(_self),
            hasUser: hasUserProxy(_self),
            getAuthMethod: getAuthMethodProxy(_self)
        };
    }

    //Returns 'project' sub-namespace
    get projects() {
        var _self = this;

        function getProxy(_self) {
            return (...args) => { return _self.getProjects(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            get: getProxy(_self)
        };
    }

    //Returns 'profile' sub-namespace
    get profile() {
        var _self = this;

        function getProxy(_self) {
            return (...args) => { return _self.getUserProfile(...args) };
        }

        function isLanguageProxy(_self) {
            return (...args) => { return _self.isLanguage(...args) };
        }

        function getLanguageProxy(_self) {
            return (...args) => { return _self.getLanguage(...args) };
        }

        function setLanguageProxy(_self) {
            return (...args) => { return _self.setLanguage(...args) };
        }

        function saveLanguageProxy(_self) {
            return (...args) => { return _self.saveLanguage(...args) };
        }

        function savePreferenceProxy(_self) {
            return (...args) => { return _self.savePreference(...args) };
        }

        function saveProxy(_self) {
            return (...args) => { return _self.saveUserProfile(...args) };
        }

        function uploadAvatar(_self) {
            return (...args) => { return _self.uploadAvatar(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            get: getProxy(_self),
            isLanguage: isLanguageProxy(_self),
            getLanguage: getLanguageProxy(_self),
            setLanguage: setLanguageProxy(_self),
            saveLanguage: saveLanguageProxy(_self),
            savePreference: savePreferenceProxy(_self),
            uploadAvatar: uploadAvatar(_self),
            save: saveProxy(_self)
        };
    }

    get supportedKeys()
    {
        var _self = this;

        function getProxy(_self) {
            return (...args) => { return _self.getSupportedKeys(...args) };
        }

        return {
            get: getProxy(_self)
        };
    }

    //Returns 'authorizedKeys' sub-namespace
    get authorizedKeys() {
        var _self = this;

        function getProxy(_self) {
            return (...args) => { return _self.getAuthorizedKeys(...args) };
        }

        function postProxy(_self) {
            return (...args) => { return _self.postAuthorizedKeys(...args) };
        }

        function deleteProxy(_self) {
            return (...args) => { return _self.deleteAuthorizedKeys(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            get: getProxy(_self),
            post: postProxy(_self),
            delete: deleteProxy(_self)
        };
    }

    //Returns 'options' sub-namespace
    get options() {
        var _self = this;

        function getWelcomedProxy(_self) {
            return (...args) => { return _self.getWelcomed(...args) };
        }

        function setWelcomedProxy(_self) {
            return (...args) => { return _self.setWelcomed(...args) };
        }

        function getLogonDestinationProxy(_self) {
            return (...args) => { return _self.getLogonDestination(...args) };
        }

        function setLogonDestinationProxy(_self) {
            return (...args) => { return _self.setLogonDestination(...args) };
        }

        function setLastLocationProxy(_self) {
            return (...args) => { return _self.setLastLocation(...args) };
        }

        function getLogonDestinationStateProxy(_self) {
            return (...args) => { return _self.getLogonDestinationState(...args) };
        }

        function isDarkModeProxy(_self) {
            return (...args) => { return _self.isDarkMode(...args) };
        }

        function setDarkModeProxy(_self) {
            return (...args) => { return _self.setDarkMode(...args) };
        }

        function isStickyTopProxy(_self) {
            return (...args) => { return _self.isStickyTop(...args) };
        }

        function setStickyTopProxy(_self) {
            return (...args) => { return _self.setStickyTop(...args) };
        }

        function applyStickyTopProxy(_self) {
            return (...args) => { return _self.applyStickyTop(...args) };
        }

        function applyDarkModeProxy(_self) {
            return (...args) => { return _self.applyDarkMode(...args) };
        }

        function getTiEditVersionProxy(_self) {
            return (...args) => { return _self.getTiEditVersion(...args) };
        }

        function setTiEditVersionProxy(_self) {
            return (...args) => { return _self.setTiEditVersion(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            getWelcomed: getWelcomedProxy(_self),
            setWelcomed: setWelcomedProxy(_self),
            getLogonDestination: getLogonDestinationProxy(_self),
            setLogonDestination: setLogonDestinationProxy(_self),
            setLastLocation: setLastLocationProxy(_self),
            getLogonDestinationState: getLogonDestinationStateProxy(_self),
            isDarkMode: isDarkModeProxy(_self),
            setDarkMode: setDarkModeProxy(_self),
            isStickyTop: isStickyTopProxy(_self),
            setStickyTop: setStickyTopProxy(_self),
            applyStickyTop: applyStickyTopProxy(_self),
            applyDarkMode: applyDarkModeProxy(_self),
            getTiEditVersion: getTiEditVersionProxy(_self),
            setTiEditVersion: setTiEditVersionProxy(_self)
        };
    }

    //Returns 'preference' sub-namespace
    get preference() {
        var _self = this;

        function getPreferenceProxy(_self) {
            return (...args) => { return _self.getPreference(...args) };
        }

        function setPreferenceProxy(_self) {
            return (...args) => { return _self.setPreference(...args) };
        }

        function deletePreferenceProxy(_self) {
            return (...args) => { return _self.deletePreference(...args) };
        }

        function initProxy(_self) {
            return (...args) => { return _self.init(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            getPreference: getPreferenceProxy(_self),
            setPreference: setPreferenceProxy(_self),
            deletePreference: deletePreferenceProxy(_self),
            init: initProxy(_self)
        };
    }

    private handleValidateTokenSuccess() {
        this.uiService.message.showInfo(
            'Your email has been confirmed, follow the steps there to complete your registration.',
            this.gfTranslateService.translate('Account Creation Request')
        );
    }

    private handleValidateTokenFail(result) {
        var msg = '';
        if (_.has(result, 'data.captcha')) {
            msg = this.gfTranslateService.translate('The CAPTCHA you entered was incorrect');
        } else if (_.has(result, 'error') && result.error.type == 'user-registration-token-invalid') {
            msg = this.gfTranslateService.translate('The submitted token is invalid.');
        } else if (_.has(result, 'error') && result.error.type == 'user-registration-token-mismatch') {
            msg = this.gfTranslateService.translate('The submitted token does not match with submitted email.');
        } else if (_.has(result, 'error') && result.error.type == 'user-registration-token-not-exist') {
            msg = this.gfTranslateService.translate('The submitted token is not registered in the system. ');
        }

        return msg;
    }

    private handleEmailSubmitSuccess(params, unixName: any = '') {
        if (unixName != 0 && unixName != '') {
            this.uiService.message.showInfo(
                'Your email change request has been accepted. You will receive an email in your current address, please follow the steps there to confirm your new email.',
                this.gfTranslateService.translate('Email Change Request')
            );
        } else {
            this.uiService.message.showInfo(
                'Your account creating request has been accepted. You will receive an email in the address you provided, follow the steps there to complete your registration.',
                this.gfTranslateService.translate('Account Creation Request')
            );
        }

    }

    private handleEmailSubmitFail(result) {
        var msg = '';
        if (_.has(result, 'data.captcha')) {
            msg = this.gfTranslateService.translate('The CAPTCHA you entered was incorrect');
        } else if (_.has(result, 'error') && result.error.type == 'user-registration-request-exists') {
            msg = this.gfTranslateService.translate('There is already a user registration request for this email');
        } else if ((_.has(result, 'error') && result.error.type == 'email_db_register') ||
            (_.has(result, 'error.email') && Array.isArray(result.error.email) && _.has(result.error.email[0], 'type') &&
                result.error.email[0].type == 'email_db_register')) {
            msg = this.gfTranslateService.translate('There is already a user registered with this email');
        } else if ((_.has(result, 'error') && result.error.type == 'email_is_disposable') ||
            (_.has(result, 'error.email') && Array.isArray(result.error.email) && _.has(result.error.email[0], 'type') &&
                result.error.email[0].type == 'email_is_disposable')) {
            msg = this.gfTranslateService.translate('Sorry, this email domain is not allowed');
        }

        return msg;
    }


    public isNobody(u) {
        return _.get(u, 'id', '') == this.constants.nobody.id;
    }

    /**
     * Given an email and a token, validates the token as:
     *  a) being associated with the provided email
     *  b) the token being valid (not expired)
     * 
     * @param {string} email 
     * @param {string} token 
     * @returns {object} returns a promise that resolves to true (token valid) or false (token invalid)
     **/
    public validateToken(email, token) {
        this.msg = '';
        var params = {
            email: email,
            token: token
        };

        return this.userRest.postToken(params)
            .then(() => {
                this.handleValidateTokenSuccess();
                return true;
            })
            .catch((result) => {
                this.msg = this.handleValidateTokenFail(result);
                throw new Error(this.msg);
            });
    }

    /**
     * Submits an email to initiate a user creation request (creates a token associated to the email). 
     * The 'validateOnly' parameter signals the backend endpoint to only check for the existence
     * of a token associated to this email, and not register it
     * 
     * @param {string} email 
     * @param {string} validateOnly 
     **/
    public submitEmail(email, unixName: any = 0, captcha, validateOnly: boolean = false) {
        var msg = '';
        var params = {
            email: email,
            captcha: captcha,
            validateonly: false
        };

        if (validateOnly) {
            params.validateonly = true;
        };

        return this.userRest.postEmail(_.isEmpty(unixName) ? 0 : unixName, params)
            .then(() => {
                if (!validateOnly) {
                    this.handleEmailSubmitSuccess(params, unixName);
                }
            })
            .catch((err) => {
                if (!validateOnly) {
                    this.msg = this.handleEmailSubmitFail(err);
                }
                throw new Error(this.msg);
            });
    }

    public getMsg() {
        return this.msg;
    }

    //public fullName(userBasic) {
    public fullName = (userBasic: any): string => {
        var ret = '';

        if (this.isNobody(userBasic)) {
            return this.gfTranslateService.translate("Nobody");
        }

        if (userBasic) {
            var firstname = _.get(userBasic, 'firstname', '');
            var lastname = _.get(userBasic, 'lastname', '');

            if (firstname == null || firstname == 'null') {
                firstname = '';
            }

            if (lastname == null || lastname == 'null') {
                lastname = '';
            }

            ret = firstname + ' ' + lastname;

            //This is a special property used for tracker item edit
            //for the case of assignees that are former members
            //if (_.get(userBasic, '_former', false) === true) {
            //    ret += ' (Former Member)';
            //}
        }
        return ret.trim();
    }

    public getNobody() {
        return this.constants.nobody;
    }

    public changePassword(unixName, confirmationHash, newPassword) {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest');

        return this.http.post(
            '/api/user/' + unixName + '/change',
            { unixName: unixName, confirmationHash: confirmationHash, password: newPassword },
            { headers: headers })
            .toPromise()
            .then((response) => {
                return _.get(response, 'data', {});
            })
            .catch((data) => {
                throw new Error(data);
            });
    }

    /** START Session namespace public methods */

    /**
     * Given a username and a password, processes the login and, if successul, sets the user
     *
     * @param  {unixName}   String
     * @return Object promise
     */
    public login(username, password) {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest')
            .set('Authorization', 'Basic ' + this.base64.encode(username + ':' + password));

        return new Promise((resolve, reject) => {
            this.http.post(
                '/api/usersession/' + username,
                null,
                { headers: headers })
                .toPromise()
                .then((response: any) => {
                    // TODO Re-enable this functionality with GTM
                    // this.window.ga('send', 'event', 'login');

                    this.setUser(response?.user);
                    this.tokenService.setToken(response?.SessionHash);

                    // Force: an auth error leaves PollService in 'idle' with a stale
                    // in-flight request, and it never restarts itself from there.
                    this.pollService.run(true);
                    resolve({ success: true });
                })
                .catch((data) => {
                    const message = (data?.error || data?.data) || null;
                    const status = data?.status;
                    reject({ success: false, message, status });
                });
        });
    }

    /**
     * Clears the current user from the global this.window object
     */
    public setNobody() {
        this.tokenService.setToken(null);
        return this.setUser(
            _.clone(this.constants.nobody)
        );
    }

    public setUser(user) {
        // NOTE:  this.window.currentUser is still set for backward compatibility
        // in the user resolve for home state.  See web/client-app/core/app.js.
        if (_.get(user, 'unixName', 'None') != _.get(this._user, 'unixName', '')) {
            this._user = user;
        }
        // Apply the user's language preference on every user set (login, session restore, refresh)
        const lang = _.get(user, 'language', '');
        if (lang && lang !== 'en') {
            this.gfTranslateService.setLanguage(lang);
        }
        return this._user;
    }


    /**
     * Logs out the current user and destroys the session
     *
     * @return Object promise
     */
    public logout() {
        var result = {
            success: false,
            message: {}
        };

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest');


        this.pollService.cancelCurrent();
        this.pollService.pause();
        return new Promise((resolve, reject) => {
            this.http.delete('/api/usersession', { headers: headers })
                .toPromise()
                .then(() => {
                    result = { success: true, message: this.messages.log_out_success };
                })
                .catch(() => {
                    result = { success: false, message: this.messages.log_out_failure };
                })
                .finally(() => {
                    // TODO Re-enable this functionality with GTM
                    // this.window.ga('send', 'event', 'logout');
                    this.setNobody();

                    this._stickyTop = false;
                    this.applyStickyTop();

                    resolve(result);
                });
        });
    }

    /**
     * Confirm the session is still live.
     *
     * With `redirect` set this is a route guard, and it refuses the way every other
     * guard does — by rejecting with a `RefusedError` for the ui-router error hook to
     * route (see docs/PERMISSION-REDIRECT.md). It deliberately does *not* navigate:
     * doing so from here raced the transition it was guarding, and the plain
     * `stateService.go` it used did not reload the `home` state, so the session the
     * login screen saw was the stale local one.
     *
     * Without `redirect` it stays a plain question: resolves true/false.
     */
    public verify(redirect) {
        const user_id = _.get(this._user, 'id', 100);
        const unixName = _.get(this._user, 'unixName', '');

        if (user_id <= 100) {
            // No local session to validate. Nobody has been proven expired, so this is
            // an ordinary "log in first", not a session-expiry notice.
            return redirect
                ? Promise.reject(RefusedError.unauthorized('No local session to validate'))
                : Promise.resolve(false);
        }

        return this.userRest.validateSession(unixName)
            .then(
                () => true,
                () => {
                    if (!redirect) return Promise.reject(false);

                    // The backend just told us this session is gone, so drop the local
                    // copy before anything re-reads hasUser(). The login state's
                    // `sessionRedirect` resolve reads exactly that, and a stale `true`
                    // there follows the return URL straight back here — which fails
                    // again, and loops.
                    this.setNobody();

                    // `sessionExpired` says we already validated, so the error hook can
                    // expire the session outright instead of spending a second
                    // /validate call re-deriving what we just proved.
                    return Promise.reject(RefusedError.expiredSession());
                }
            );
    }

    /**
     * Returns the active session unixName
     */
    public getUnixName() {
        return _.get(this._user, 'unixName', this.constants.nobody.unixName);
    }

    /**
     * Returns the active user unixName
     */
    public getUserId() {
        return _.get(this._user, 'id', this.constants.nobody.id);
    }

    /**
     * Returns the active user
     */
    public getUser() {
        return this._user || this.constants.nobody;
    }

    /**
     * Returns true if the session user is a site admin, false otherwise
     */
    public isSiteAdmin() {
        return _.get(this._user, 'isSiteAdmin', false);
    }

    /**
     * Returns the authentication method used for the current session (e.g. 'db', 'ldap', 'sso', 'oidc'),
     * or null if not available.
     */
    public getAuthMethod() {
        return this._user?.authMethod ?? null;
    }

    /**
     * Recovers a session from the cookies and sets the current user
     */
    public recover(force: boolean = false) {
        if (this.currentUserPromise && !force) return this.currentUserPromise;

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest');

        this.currentUserPromise = this.http.get('/api/usersession', { headers: headers }).toPromise();

        return new Promise((resolve, reject) => {
            this.currentUserPromise
                .then((response: any) => {
                    this.tokenService.setToken(response?.SessionHash);

                    if (response.id) {
                        this.setUser(response);
                        resolve({ success: true });
                    } else {
                        resolve({ success: true, newSSO: true });
                    }
                })
                .catch(() => {
                    this.setNobody();
                    resolve({ success: false });
                });
        });
    }

    /**
     * Checks if there's an active user session
     */
    public hasUser() {
        return (_.get(this._user, 'id', 100) > 100);
    }

    /** END Session namespace public methods */

    /** START Projects namespace public methods */

    /**
     * Given a username, retrieves the projects the user is member of
     *
     * @param  {unixName}   String
     * @return Object promise
     */
    public getProjects(unixName) {
        return new Promise((resolve, reject) => {
            this.userRest.getProjects(unixName)
                .then((response: any) => {
                    resolve({ success: true, projects: response.items });
                })
                .catch(() => {
                    resolve({ success: false, message: this.messages.project_data_failure });
                });
        });
    }

    /** END Projects namespace public methods */

    /** START Methods for namespace 'authorizedKeys' */

    public getAuthorizedKeys(unixName: string) {
        return new Promise((resolve, reject) => {
            this.userRest.getSshKeys(unixName)
                .then(function (response) {
                    resolve({ success: true, data: response });
                })
                .catch(() => {
                    reject({ success: false, message: this.messages.ssh_key_error_get });
                });
        });
    }

    public postAuthorizedKeys(unixName: string, authorizedKeys: any) {
        return new Promise((resolve, reject) => {
            this.userRest.postSshKeys(unixName, { authorized_keys: authorizedKeys })
                .then((response) => {
                    resolve({ success: true, response: response });
                })
                .catch((response: any) => {
                    resolve({
                        success: false,
                        message: this.messages.ssh_key_error_post,
                        response: response
                    });
                });
        });
    }

    public getSupportedKeys(unixName: string)
    {
        return new Promise((resolve, reject) => {
            this.userRest.getSupportedKeys(unixName).
                then((response) => {
                    resolve({ success: true, response: response });
                })
                .catch((response: any) => { resolve({ success: false, response: response }) });
        });
    }

    public deleteAuthorizedKeys(unixName) {
        return new Promise((resolve, reject) => {
            this.userRest.deleteSshKeys(unixName)
                .then((response) => {
                    resolve({ success: true, response: response });
                })
                .catch(() => {
                    resolve({ success: false, message: this.messages.ssh_key_error_post });
                });
        });
    }

    /** END Methods for namespace 'authorizedKeys' */

    /** START Profile namespace methods */

    /**
     * Given a username, retrieves the projects the user is member of
     *
     * @param  {unixName}   String
     * @return Object promise
     */
    public getUserProfile(unixName) {
        return new Promise((resolve, reject) => {
            this.userRest.getFull(unixName)
                .then((response) => {
                    resolve({ success: true, data: response });
                })
                .catch(() => {
                    resolve({ success: false, message: this.messages.project_data_failure });
                });
        });
    }

    public isLanguage(lang) {
        return lang == this.getLanguage();
    }

    public getLanguage() {
        return this.getUser().language;
    }

    public setLanguage(lang) {
        if (!this.isLanguage(lang)) {
            const p: any = this.saveLanguage({
                unixName: this.getUnixName(),
                language: lang
            });
            
            p.then(() =>
                this.stateService.reload(),
                () => {}
            );
        }
    }

    public saveLanguage(userData) {
        var language = _.get(userData, 'language', false);
        var unixName = _.get(userData, 'unixName', false)
        if (!language || !unixName)
            return false;

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest');

        return new Promise((resolve, reject) => {
            this.http.post(
                '/api/user/' + userData.unixName, userData, { headers: headers }
            )
                .toPromise()
                .then(() => {
                    this.getUser().language = language;

                    moment.locale(language);//@TODO this will need mapping convertion between angular i10n and moment locale config. Check TI #28861
                    // this.tmhDynamicLocaleService.set(language)
                    //     .then(() => {
                    //         this.stateService.reload();
                    //     });

                    this.gfTranslateService.setLanguage(language).then(() => {
                        resolve({ success: true, message: this.messages.language_changed_success });
                    });
                })
                .catch(() => {
                    resolve({ success: false, message: this.messages.language_changed_error });
                });
        });
    }

    public savePreference(unixName, prefName, value) {
        return this.userRest.saveUserPreference(unixName, prefName, value);
    }

    public uploadAvatar(unixName, file) {
        const formData = new FormData();
        formData.set('unixName', unixName);
        formData.set('useCustomAvatar', 'Y');
        formData.append('file', file, file.name);

        const url = `/api/user/${ unixName }/details`;
        return new Promise((resolve, reject) => {
            this.http.post(url, formData)
            .toPromise()
            .then((response: any) => {
                //File uploaded successfully
                resolve({ success: true, data: response });
            })
            .catch((err) => {
                reject({ success: false, errorMessage: err, status: err.status });
            });
        });        
    }

    public saveUserProfile(userData) {
        var unixName = _.get(userData, 'unixName', false);

        if (!unixName)
            return false;

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-Requested-With', 'XMLHttpRequest');

        return new Promise((resolve, reject) => {
            this.http.post('/api/user/' + unixName + '/details', userData, { headers: headers })
                .toPromise()
                .then(() => {
                    resolve({ success: true, message: this.messages.user_post_success });
                })
                .catch(() => {
                    resolve({ success: false, message: this.messages.user_post_failure });
                });
        });
    }

    /** END Profile namespace methods */

    /** START Options namespace methods */



    public getLastVersionCheck() {
        return this._lastVersionCheck;
    }

    public fetchLastVersionCheck() {
        return this.getPreference('admin.version-check', 0)
            .then((result) => {
                const val = result?.preferenceValue;
                this._lastVersionCheck = (val && val != 'false')? val : 0;
            }, (response) => {
                console.log(response);
                this._lastVersionCheck = 0;
            });
    }

    public setLastVersionCheck() {
        const newSetting = Math.floor(new Date().getTime() / 1000); // seconds vs millis
        return this.setPreference('admin.version-check', newSetting)
            .then(() => {
                this._lastVersionCheck = newSetting;
            }, (err) => console.log(err));
    }

    public addVersionCheckWallPost(toUnixName, msg) {
        const url = `/api/user/${toUnixName}/wall`;

        const body = {
            to: toUnixName,
            msg: msg
        };

        this.http.post(url, body).toPromise()
            .then(
                () => {},
                function (response) {
                    console.log("Version check POST failed", response);
                }
            );
    }

    public checkVersion() {
        // Set internally to prevent multiple checks (race cond)
        this._lastVersionCheck = Math.floor(new Date().getTime() / 1000);
        const url = 'https://next.gforge.com/site/version';

        const appVersionDate = _.get(this.window, 'appVersionDate', '');
        const body = {
            h: _.get(this.window, 'location.hostname', 'localhost'),
            e: _.toLower(this._user.email),
            v: _.get(this.window, 'appRelease', 'unknown') + ' ' + _.get(this.window, 'appVersion', 'unknown') + ' ' + appVersionDate
        };

        this.http.post(url, body).toPromise()
            .then(
                (response) => {
                    const latestVersion = _.get(response, 'version', '');
                    const latestVersionDate = _.get(response, 'date', appVersionDate);
                    if (latestVersionDate > appVersionDate) {
                        const msg = `An updated version of GForge Next (${latestVersion}) is available.  Check https://gforge.com/downloads for more information.`;

                        this.addVersionCheckWallPost(
                            this.getUnixName(),
                            msg
                        );
                    }
                }, function (rejected) {
                    console.log(rejected);
                }
            ).finally(() => { 
                this.setLastVersionCheck();
            });
    }

    public getWelcomed() {
        return this._welcomed;
    }

    public setWelcomed() {
        return new Promise((resolve, reject) => {
            this.setPreference('welcomed', 1)
                .then(() => {
                    this._welcomed = 1;
                    resolve(null);
                })
                .catch((response) => {
                    console.log(response);
                    reject();
                });
        });
    }

    public getLogonDestination() {
        return this._logonDestination;
    }

    public setLogonDestination(dest) {
        if (this._logonDestination != dest) {
            this.setPreference('logon-destination', dest)
                .then(() => {
                    this._logonDestination = dest;
                    this.uiService.message.showSuccess("Your logon preference has been updated", "Set Logon Destination");
                })
                .catch((response) => {
                    console.log(response);
                    this.uiService.message.showError("Sorry, could not update your logon preference", "Set Logon Destination");
                });
        }
        this._logonDestination = dest;
        return this._logonDestination;
    }

    public setLastLocation(name, params) {
        // Don't record this location if it's in the blacklist
        if (_.includes(this._lastLocationExceptions, name)) {
            return false;
        }

        return this.setPreference(
            'last-location',
            {
                name: name,
                params: _.pickBy(params)
            }
        ).catch(
            (err) => { console.log(err); /* Do nothing */}
        );
    }

    public getLogonDestinationState() {
        var unixName = this.getUnixName();
        if (!this._welcomed && !this._hasProjects) {
            this.setWelcomed();
            return { name: 'home.user.welcome', params: { unixName: unixName } };
        }
        switch (this._logonDestination) {
            case 'user': return { name: 'home.user.userhome', params: { unixName: unixName } };

            case 'last':
                // Fall through to default if the location is in the blacklist
                if (!_.includes(this._lastLocationExceptions, this._lastLocation.name)) {
                    return this._lastLocation;
                }

            // including 'todo'
            default: return { name: 'home.user.my', params: { unixName: unixName } };
        }
    }

    public isDarkMode() {
        return this._darkMode;
    }

    public setDarkMode(darkMode) {
        if (darkMode != this._darkMode) {
            this.setPreference('dark-mode', darkMode)
                .then(() => {
                    this._darkMode = darkMode;
                    this.uiService.message.showSuccess("Dark Mode is now " + (this._darkMode ? 'on' : 'off'), "Dark Mode");
                    this.applyDarkMode();
                })
                .catch((response) => {
                    this.uiService.message.showError("Sorry, could not update your Dark Mode setting.", "Dark Mode");
                });
        }
        return this._darkMode;
    }

    public isStickyTop() {
        return this._stickyTop == true;
    }

    public setStickyTop(stickyTop) {
        if (stickyTop != this._stickyTop) {
            return new Promise((resolve, reject) => {
                this.setPreference('sticky-top', stickyTop)
                    .then(() => {
                        this._stickyTop = stickyTop;
                        var on = 'on', will = 'will not';
                        if (!this._stickyTop) {
                            on = 'off';
                            will = 'will';
                        }
                        this.uiService.message.showSuccess("Sticky Top is " + on + '.  The GForge Next heading ' + will + ' scroll with the page.', "Sticky Top");
                        this.applyStickyTop();

                        resolve(null);
                    })
                    .catch((response) => {
                        console.log(response);
                        this.uiService.message.showError("Sorry, could not update your Sticky Top setting.", "Sticky Top");
                        reject();
                    });
            });
        }
    }

    public applyStickyTop() {
        var bodyElem = $('body');
        if (this._stickyTop) {
            if (bodyElem && bodyElem.addClass) bodyElem.addClass('sticky-top');
        } else {
            if (bodyElem && bodyElem.removeClass) bodyElem.removeClass('sticky-top');
        }
        return this._stickyTop;
    }

    public getSSOID() {
        return new Promise((resolve, reject) => {
            this.userRest.getSso()
                .then(function (response) {
                    resolve({ success: true, data: response });
                })
                .catch(() => {
                    reject({ success: false, message: "Invalid sso id" });
                });
        });
    }

    public deleteUser(unixName) {
        return new Promise((resolve, reject) => {
            this.userRest.delete(unixName)
                .then(function(response) {
                    resolve( {success: true, data: response});
                })
                .catch(() => { reject ({ success: false, message: "Failed to delete account"})});
        });
    }

    public applyDarkMode() {
        var bodyElem = $('body');
        if (this._darkMode) {
            if (bodyElem && bodyElem.addClass) $('body').addClass('dark');
        } else {
            if (bodyElem && bodyElem.removeClass) $('body').removeClass('dark');
        }
    }

    public getTiEditVersion() {
        return this._tiEditVersion;
    }

    public setTiEditVersion(version) {
        if (this._tiEditVersion != version) {
            this._tiEditVersion = version;
            this.setPreference('tiedit-version', version);
        }
    }

    /** END Options namespace methods */

    /** START Preference namespace methods */

    public getPreference(preferenceName, defaultValue) {
        return this.userRest.getUserPreference(this.getUnixName(), preferenceName,
            { default: defaultValue || false });
    }

    public setPreference(preferenceName, preferenceValue) {
        return this.userRest.saveUserPreference(this.getUnixName(), preferenceName, preferenceValue);
    }

    public deletePreference(preferenceName) {
        return this.userRest.deleteUserPreference(this.getUnixName(), preferenceName);
    }

    public init() {
        if (!this._user ||
            this._user.id == 100) {
            // No user, default settings
            this._darkMode = false;
            this._tiEditVersion = 2;
            this.applyDarkMode();
            return Promise.resolve();
        } else {
            var promises = [];

            promises.push(
                this.userRest.getUserPreference(this.getUnixName(), 'sticky-top', { default: 0 })
                    .then((result) => {
                        this._stickyTop = (_.get(result, 'preferenceValue', 0) != 0);
                        this.applyStickyTop();
                    })
                    .catch(() => {
                        this._stickyTop = false;
                    })
            );

            var defaultDest = 'user';
            promises.push(
                this.userRest.getUserPreference(this.getUnixName(), 'logon-destination',
                    { default: defaultDest })
                    .then((result) => {
                        this._logonDestination = (_.get(result, 'preferenceValue', defaultDest));
                    })
                    .catch(() => {
                        this._logonDestination = defaultDest;
                    })
            );

            const defaultWelcomed = 0;
            promises.push(
                this.userRest.getUserPreference(this.getUnixName(), 'welcomed',
                    { default: defaultWelcomed })
                    .then((result) => {
                        this._welcomed = parseInt(_.get(result, 'preferenceValue', defaultWelcomed));
                    })
                    .catch(() => {
                        this._welcomed = defaultWelcomed;
                    })
            );

            promises.push(
                this.userRest.getProjects(this.getUnixName(), { max: 1 })
                    .then((result) => {
                        this._hasProjects = _.get(result, 'items', []).length > 0;
                    })
                    .catch(() => {
                        this._hasProjects = false;
                    })
            );

            var defaultLast = {
                name: 'home.user.my',
                params: { unixName: this.getUnixName() }
            };
            promises.push(
                this.userRest.getUserPreference(this.getUnixName(), 'last-location',
                    { default: defaultLast })
                    .then((result) => {
                        this._lastLocation = _.get(result, 'preferenceValue', defaultLast);
                    })
                    .catch(() => {
                        this._lastLocation = defaultLast;
                    })
            );

            promises.push(
                this.userRest.getUserPreference(this._user.unixName, 'dark-mode',
                    { default: 0 })
                    .then((result) => {
                        this._darkMode = (_.get(result, 'preferenceValue', 0) != 0);
                        this.applyDarkMode();
                    })
                    .catch(() => {
                        this._darkMode = false;
                    })
            );

            promises.push(
                this.userRest.getUserPreference(this._user.unixName, 'tiedit-version',
                    { default: 2 })
                    .then((result) => {
                        this._tiEditVersion = _.get(result, 'preferenceValue', 2);
                    })
                    .catch(() => {
                        this._tiEditVersion = 2;
                    })
            );

            const appEnv = _.get(this.siteConfigService.getSiteConfig(), 'appEnvironment', 'production');
            if (this.isSiteAdmin() && appEnv == 'production') {
                promises.push(
                    this.fetchLastVersionCheck()
                        .then(() => {
                            const intervalTicks = 60 * 60 * 24 * 14;  // Two weeks
                            const now = Math.floor(new Date().getTime() / 1000);  // millis vs seconds
                            if (now - this._lastVersionCheck > intervalTicks) {
                                this.checkVersion();
                            }
                        }, function (response) {
                            console.log(response);
                        })
                );
            }

            return Promise.all(promises);
        }
    }

    /** END Preference namespace methods */
}
